import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip token refresh for auth endpoints that don't use bearer tokens
    const authExemptUrls = ['/auth/login', '/auth/2fa/verify', '/auth/signup', '/auth/forgot-password', '/auth/reset-password'];
    const isAuthExempt = authExemptUrls.some(url => originalRequest.url?.includes(url));

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthExempt) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(`${API_URL}/auth/refresh-token`, {
          refreshToken,
        });

        const { token, refreshToken: newRefreshToken } = response.data.data;

        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle 503 maintenance mode — redirect to maintenance page
    if (error.response?.status === 503 && error.response?.data?.maintenance) {
      // Store maintenance info for the maintenance page
      sessionStorage.setItem('maintenance_info', JSON.stringify(error.response.data.maintenance));
      if (window.location.pathname !== '/maintenance') {
        window.location.href = '/maintenance';
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  resetAccount: (data) => api.post('/auth/reset-account', data),
  // Password Reset (AWS-based, NO Supabase)
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  validateResetToken: (token) => api.get('/auth/validate-reset-token', { params: { token } }),
  // Two-Factor Authentication
  get2faStatus: () => api.get('/auth/2fa/status'),
  begin2faSetup: () => api.post('/auth/2fa/setup'),
  verify2faSetup: (totpCode) => api.post('/auth/2fa/enable', { totpCode }),
  verify2faLogin: (data) => api.post('/auth/2fa/verify', data),
  regenerateRecoveryCodes: (password) => api.post('/auth/2fa/recovery-codes', { password }),
  markRecoveryCodesViewed: () => api.post('/auth/2fa/recovery-codes/viewed'),
  disable2fa: (password, verificationCode) => api.post('/auth/2fa/disable', { password, verificationCode }),
};

export const ledgerAPI = {
  getAll: (params) => api.get('/ledgers', { params }),
  getOne: (id) => api.get(`/ledgers/${id}`),
  create: (data) => api.post('/ledgers', data),
  update: (id, data) => api.put(`/ledgers/${id}`, data),
  archive: (id) => api.post(`/ledgers/${id}/archive`),
  unarchive: (id) => api.post(`/ledgers/${id}/unarchive`),
  delete: (id) => api.delete(`/ledgers/${id}`),
};

export const transactionAPI = {
  getAll: (params) => api.get('/transactions', { params }),
  getOne: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post('/transactions', data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  delete: (id) => api.delete(`/transactions/${id}`),
  getSummary: (params) => api.get('/transactions/summary', { params }),
};

export const categoryAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getOne: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  bookmark: (id, sortOrder) => api.post(`/categories/${id}/bookmark`, { sort_order: sortOrder }),
  unbookmark: (id) => api.delete(`/categories/${id}/bookmark`),
  getBookmarks: () => api.get('/categories/bookmarks'),
};

export const reminderAPI = {
  getAll: (params) => api.get('/reminders', { params }),
  getUpcoming: (params) => api.get('/reminders/upcoming', { params }),
  getOne: (id) => api.get(`/reminders/${id}`),
  create: (data) => api.post('/reminders', data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  markComplete: (id) => api.post(`/reminders/${id}/complete`),
  delete: (id) => api.delete(`/reminders/${id}`),
};

export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.post(`/notifications/${id}/mark-read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
};

export const transferAPI = {
  getAll: (params) => api.get('/transfers', { params }),
  getOne: (id) => api.get(`/transfers/${id}`),
  create: (data) => api.post('/transfers', data),
  delete: (id) => api.delete(`/transfers/${id}`),
};

export const reportAPI = {
  getDaily: (params) => api.get('/reports/daily', { params }),
  getMonthly: (params) => api.get('/reports/monthly', { params }),
  getYearly: (params) => api.get('/reports/yearly', { params }),
  getCategory: (params) => api.get('/reports/category', { params }),
  getVendor: (params) => api.get('/reports/vendor', { params }),
};

export const vendorAPI = {
  getAll: () => api.get('/vendors'),
  getOne: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  /**
   * Single-Action Vendor Entry (ENTERPRISE RULE)
   * entry_type: 'expense' | 'income' | 'payable' | 'receivable'
   * For expense/income: Creates transaction + updates vendor ledger atomically
   */
  createEntry: (vendorId, data) => api.post(`/vendors/${vendorId}/entry`, data),
  /**
   * Get all transactions for a specific vendor
   * Returns transactions + summary (income, expense, payables, receivables)
   */
  getTransactions: (vendorId, params) => api.get(`/vendors/${vendorId}/transactions`, { params }),
  /**
   * Get System Payables Summary (miscellaneous dues not tied to specific vendors)
   * Returns: total outstanding, category-wise breakdown, optional transactions
   * @param {boolean} includeTransactions - Include transaction details
   * @param {string} categoryId - Filter transactions by category (optional)
   */
  getSystemPayables: (includeTransactions = false, categoryId = null) =>
    api.get('/vendors/system-payables', {
      params: {
        include_transactions: includeTransactions,
        category_id: categoryId,
      },
    }),
};

export const budgetAPI = {
  getAll: (params) => api.get('/budgets', { params }),
  getOne: (id) => api.get(`/budgets/${id}`),
  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.put(`/budgets/${id}`, data),
  delete: (id) => api.delete(`/budgets/${id}`),
  getStatus: (params) => api.get('/budgets/status', { params }),
  checkThresholds: () => api.get('/budgets/check-thresholds'),
  getAlerts: (params) => api.get('/budgets/alerts', { params }),
  markAlertRead: (id) => api.post(`/budgets/alerts/${id}/mark-read`),
  markAllAlertsRead: () => api.post('/budgets/alerts/mark-all-read'),
  getHistory: (params) => api.get('/budgets/history', { params }),
  getReport: (params) => api.get('/budgets/report', { params }),
  getInsights: () => api.get('/budgets/insights'),
  autoRoll: () => api.post('/budgets/auto-roll'),
  snapshot: (id) => api.post(`/budgets/${id}/snapshot`),
};

export const adminAPI = {
  // Dashboard & Statistics
  getStatistics: () => api.get('/app-admin/statistics'),

  // Phase 2D: Intelligence Metrics (MRR, ARPU, Churn, Retention)
  getIntelligenceMetrics: () => api.get('/app-admin/intelligence-metrics'),

  // Phase 2C: System Health
  getSystemHealth: () => api.get('/app-admin/system-health'),

  // Phase 2B: Admin Audit Logs
  getAuditLogs: (params) => api.get('/app-admin/audit-logs', { params }),

  // Trigger metrics calculation (admin-only)
  triggerMetricsCalculation: (targetDate) =>
    api.post('/app-admin/calculate-metrics', { target_date: targetDate }),

  // User Management
  listUsers: (params) => api.get('/app-admin/users', { params }),
  updateUserPlan: (userId, data) => api.put(`/app-admin/users/${userId}/plan`, data),
  updateUserRole: (userId, data, headers) => api.put(`/app-admin/users/${userId}/role`, data, { headers }),
  updateUserStatus: (userId, data, headers) => api.put(`/app-admin/users/${userId}/status`, data, { headers }),
  getAssignableRoles: () => api.get('/app-admin/assignable-roles'),

  // Plans
  getPlans: () => api.get('/app-admin/plans'),
  getMyPlan: () => api.get('/auth/my-plan'),

  // Admin Settings
  getAdminSettings: () => api.get('/app-admin/settings'),
  updateAdminSettings: (settings, headers) => api.put('/app-admin/settings', { settings }, { headers }),

  // Maintenance management
  scheduleMaintenance: (data) => api.post('/app-admin/maintenance/schedule', data),
  cancelMaintenance: () => api.post('/app-admin/maintenance/cancel'),
  activateMaintenanceNow: (data, headers) => api.post('/app-admin/maintenance/activate-now', data, { headers }),
  endMaintenance: () => api.post('/app-admin/maintenance/end'),
  getMaintenanceHistory: () => api.get('/app-admin/maintenance/history'),
  getActiveUsers: () => api.get('/app-admin/maintenance/active-users'),
  forceLogoutAll: (headers) => api.post('/app-admin/maintenance/force-logout', {}, { headers }),
  forceLogoutUser: (userId, headers) => api.post('/app-admin/maintenance/force-logout', { user_id: userId }, { headers }),
};

export const maintenanceAPI = {
  getStatus: () => api.get('/maintenance/status'),
};

export const uploadAPI = {
  uploadReceipt: (formData) => api.post('/uploads/receipt', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getReceipts: (params) => api.get('/uploads/receipts', { params }),
  deleteReceipt: (id) => api.delete(`/uploads/receipt/${id}`),
};

export default api;
